/**
 * FRW Rave Guide — Behavior-driven discovery hints.
 * Contextual micro-hints triggered by browsing behavior.
 * Reads existing frw_* localStorage keys (never writes to them).
 * Own state uses frw_hints_ prefix in localStorage + sessionStorage.
 *
 * Depends on: page context from window.__raveGuide (set by rave-guide.liquid).
 */
(function () {
  'use strict';

  var CFG = window.__raveGuide || {};
  var IS_COLLECTION = CFG.pageType === 'collection';
  var IS_PRODUCT = CFG.pageType === 'product';
  if (!IS_COLLECTION && !IS_PRODUCT) return;

  // Low-power device check — disable animations
  var LOW_POWER = (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) ||
                  (navigator.deviceMemory && navigator.deviceMemory <= 2);

  // ─── STORAGE HELPERS ───
  function isDismissed(id) { return localStorage.getItem('frw_hints_' + id + '_dismissed') === '1'; }
  function dismiss(id) { localStorage.setItem('frw_hints_' + id + '_dismissed', '1'); }
  function sGet(k) { return sessionStorage.getItem('frw_hints_session_' + k); }
  function sSet(k, v) { sessionStorage.setItem('frw_hints_session_' + k, v || '1'); }
  function sInc(k) { var n = parseInt(sGet(k) || '0', 10) + 1; sSet(k, String(n)); return n; }

  // ─── READ EXISTING FRW STATE (never write) ───
  function hasBudget() { return !!localStorage.getItem('frw_budget_mode'); }
  function hasPersona() { try { var p = JSON.parse(localStorage.getItem('frw_persona') || '{}'); return !!p.archetype; } catch(e) { return false; } }
  function hasViewedProducts() { try { var v = JSON.parse(localStorage.getItem('viewedProducts') || '[]'); return v.length > 0; } catch(e) { return false; } }
  function hasPerfectFit() { return !!localStorage.getItem('frw_perfect_fit') || !!localStorage.getItem('frw_quick_size'); }
  function getPerfectFit() { try { var qs = JSON.parse(localStorage.getItem('frw_quick_size') || 'null'); if (qs && qs.size) return qs; return JSON.parse(localStorage.getItem('frw_perfect_fit')); } catch(e) { return null; } }
  function hasShoppingMode() { try { var s = localStorage.getItem('frw_shopping_mode'); if (!s) return false; var d = JSON.parse(s); return d && new Date(d.end) >= new Date(); } catch(e) { return false; } }
  function getShoppingMode() { try { var s = localStorage.getItem('frw_shopping_mode'); if (!s) return null; var d = JSON.parse(s); if (d && new Date(d.end) >= new Date()) return d; return null; } catch(e) { return null; } }

  // ─── ANALYTICS ───
  function track(action, hintId, extra) {
    if (typeof dataLayer === 'undefined') return;
    var evt = { event: 'frw:hint_' + action, hint_id: hintId };
    if (extra) { for (var k in extra) { if (extra.hasOwnProperty(k)) evt[k] = extra[k]; } }
    dataLayer.push(evt);
  }

  // ─── HINT MANAGER ───
  var activeHint = null;
  var activeTimer = null;

  function showHint(id, el, duration) {
    if (activeHint) return false;
    if (isDismissed(id)) return false;
    if (id !== 'size_check' && sGet(id + '_shown')) return false;

    activeHint = { id: id, el: el };
    sSet(id + '_shown');
    track('shown', id);

    activeTimer = setTimeout(function () { closeHint('auto'); }, duration || 8000);
    return true;
  }

  function closeHint(reason) {
    if (!activeHint) return;
    var h = activeHint;
    clearTimeout(activeTimer);

    h.el.classList.add('rg-exit');
    setTimeout(function () { if (h.el.parentNode) h.el.parentNode.removeChild(h.el); }, 300);

    // Clean up any pulse/glow classes we added to targets
    document.querySelectorAll('.rg-pulse, .rg-glow').forEach(function (el) {
      el.classList.remove('rg-pulse', 'rg-glow');
    });

    if (reason === 'dismiss') dismiss(h.id);
    track(reason === 'click' ? 'clicked' : 'dismissed', h.id);

    activeHint = null;
    activeTimer = null;
  }

  // ─── RENDER HELPERS ───
  var TOAST_STYLE = 'position:fixed;bottom:90px;left:50%;transform:translateX(-50%);z-index:910;' +
    'max-width:340px;width:calc(100% - 32px);padding:14px 18px;background:#0d0d0f;' +
    'border:1px solid rgba(224,64,251,.25);border-radius:12px;box-shadow:0 8px 32px rgba(0,0,0,.6);' +
    'text-align:center;font-family:var(--font-body--family,Montserrat,sans-serif);font-size:13px;' +
    'line-height:1.4;color:#f5f7f9;cursor:pointer;';

  var CHIP_STYLE = 'display:inline-flex;align-items:center;gap:8px;padding:10px 16px;' +
    'background:rgba(224,64,251,.06);border:1px solid rgba(224,64,251,.2);border-radius:100px;' +
    'cursor:pointer;margin:12px 0;font-family:var(--font-body--family,Montserrat,sans-serif);' +
    'font-size:13px;line-height:1.4;color:#f5f7f9;';

  var BADGE_STYLE = 'display:inline-flex;align-items:center;gap:6px;padding:6px 12px;' +
    'background:rgba(224,64,251,.08);border:1px solid rgba(224,64,251,.2);border-radius:8px;' +
    'font-size:12px;margin-bottom:8px;cursor:pointer;font-family:var(--font-body--family,Montserrat,sans-serif);' +
    'color:#f5f7f9;';

  var TOOLTIP_STYLE = 'position:fixed;z-index:910;max-width:220px;padding:10px 14px;background:#0d0d0f;' +
    'border:1px solid rgba(255,255,255,.1);border-radius:12px;box-shadow:0 4px 24px rgba(0,0,0,.5);' +
    'font-family:var(--font-body--family,Montserrat,sans-serif);font-size:13px;line-height:1.4;color:#f5f7f9;';

  var STYLES = {
    'rg-toast': TOAST_STYLE,
    'rg-chip': CHIP_STYLE,
    'rg-badge': BADGE_STYLE,
    'rg-tooltip': TOOLTIP_STYLE
  };

  function makeEl(tag, cls, html) {
    var el = document.createElement(tag);
    el.className = 'rg-hint ' + cls;
    if (STYLES[cls]) el.setAttribute('style', STYLES[cls]);
    if (html) el.innerHTML = html;
    return el;
  }

  function addDismiss(el, id) {
    var btn = document.createElement('button');
    btn.className = 'rg-dismiss';
    btn.setAttribute('aria-label', 'Dismiss');
    btn.innerHTML = '&times;';
    btn.addEventListener('click', function (e) { e.stopPropagation(); closeHint('dismiss'); });
    el.appendChild(btn);
  }

  function positionAbove(tooltip, target) {
    var r = target.getBoundingClientRect();
    // Measure tooltip height (already in DOM) to position above target
    var th = tooltip.offsetHeight || 48;
    tooltip.style.left = Math.max(8, Math.min(
      window.innerWidth - 228,
      r.left + r.width / 2 - 110
    )) + 'px';
    tooltip.style.top = (r.top - th - 12) + 'px';
    tooltip.classList.remove('rg-tooltip--below');
    // If off-screen top, flip below
    requestAnimationFrame(function () {
      var tr = tooltip.getBoundingClientRect();
      if (tr.top < 8) {
        tooltip.style.top = (r.bottom + 12) + 'px';
        tooltip.classList.add('rg-tooltip--below');
      }
    });
  }

  function pulseTarget(target) {
    if (!LOW_POWER && target) target.classList.add('rg-pulse');
  }

  function glowTarget(target) {
    if (!LOW_POWER && target) target.classList.add('rg-glow');
  }

  // ─── COLLECTION HINTS ───

  // 1. budget_intro — scroll past ~60% of visible cards, no budget, no click
  function setupBudgetIntro() {
    if (hasBudget() || sGet('budget_hinted') || isDismissed('budget_intro')) return;

    var cards = document.querySelectorAll('.product-grid__item');
    if (cards.length < 6) return; // Need at least 6 cards

    var idx = Math.min(cards.length - 1, Math.max(5, Math.floor(cards.length * 0.6)));
    var sentinel = cards[idx];
    var observer = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();

      // Only fire if no product card was clicked
      if (sGet('product_clicked')) return;
      if (hasBudget() || sGet('budget_hinted')) return;

      var pill = document.getElementById('budgetPill');
      if (!pill) return;

      sSet('budget_hinted');
      var toast = makeEl('div', 'rg-toast', '<span class="rg-accent">Try setting a budget</span> — we\'ll sort what fits');
      addDismiss(toast, 'budget_intro');
      toast.addEventListener('click', function () { closeHint('click'); pill.click(); });
      document.body.appendChild(toast);
      pulseTarget(pill);

      if (!showHint('budget_intro', toast, 8000)) {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }
    }, { threshold: 0.5 });
    observer.observe(sentinel);
  }

  // 2. persona_nudge — DISABLED until quiz modal is wired up
  function setupPersonaNudge() {
    return; // TODO: re-enable once quiz-modal.liquid is rendered on collection pages
  }

  // 3. comeback_budget — return visitor with viewedProducts, no budget
  function setupComebackBudget() {
    if (hasBudget() || !hasViewedProducts() || sGet('budget_hinted') || isDismissed('comeback_budget')) return;

    var pill = document.getElementById('budgetPill');
    if (!pill) return;

    sSet('budget_hinted');
    var toast = makeEl('div', 'rg-toast', '<span class="rg-accent">Plan your festival budget</span> — we\'ll curate your picks');
    addDismiss(toast, 'comeback_budget');
    toast.addEventListener('click', function () { closeHint('click'); pill.click(); });
    document.body.appendChild(toast);
    pulseTarget(pill);

    if (!showHint('comeback_budget', toast, 8000)) {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }
  }

  // ─── PDP HINTS ───

  // 4. fit_finder — 3+ PDP views, no Perfect Fit profile
  function setupFitFinder() {
    if (hasPerfectFit() || isDismissed('fit_finder')) return;

    var views = parseInt(sGet('pdp_views') || '0', 10);
    if (views < 3) return;

    var trigger = document.querySelector('.pf-trigger');
    if (!trigger) return;

    var chip = makeEl('div', 'rg-chip',
      '<span class="rg-accent">Your perfect size</span> — 30 seconds, zero guessing');
    chip.addEventListener('click', function () { closeHint('click'); trigger.click(); });
    trigger.parentNode.insertBefore(chip, trigger);
    glowTarget(trigger);

    if (!showHint('fit_finder', chip, 8000)) {
      if (chip.parentNode) chip.parentNode.removeChild(chip);
    }
  }

  // 5. festival_mode — festival within 30 days, no shopping mode set
  function setupFestivalMode() {
    if (hasShoppingMode() || isDismissed('festival_mode')) return;

    var fest = CFG.nearestFestival;
    if (!fest || !fest.name) return;

    var smEl = document.getElementById('shopping-mode');
    if (!smEl) return;

    var badge = makeEl('div', 'rg-badge',
      'Going to <span class="rg-accent">' + fest.name + '</span>? Lock in your ship date');
    badge.addEventListener('click', function () {
      closeHint('click');
      var openBtn = document.getElementById('sm-open');
      if (openBtn) openBtn.click();
    });
    smEl.parentNode.insertBefore(badge, smEl);

    if (!showHint('festival_mode', badge, 8000)) {
      if (badge.parentNode) badge.parentNode.removeChild(badge);
    }
  }

  // 6. rave_mix — shopping mode active, hasn't clicked Spotify this session
  function setupRaveMix() {
    if (!hasShoppingMode() || sGet('spotify_clicked') || isDismissed('rave_mix')) return;

    var mode = getShoppingMode();
    if (!mode || !mode.name) return;

    var spotify = document.getElementById('sm-spotify');
    if (!spotify || !spotify.href) return;

    // 10s delay
    setTimeout(function () {
      if (activeHint) return; // Don't interrupt
      if (sGet('rave_mix_shown')) return;

      var tip = makeEl('div', 'rg-tooltip',
        'Pre-rave mood: <span class="rg-accent">' + mode.name + ' playlist</span>');
      addDismiss(tip, 'rave_mix');
      tip.addEventListener('click', function () {
        closeHint('click');
        sSet('spotify_clicked');
        spotify.click();
      });
      document.body.appendChild(tip);
      positionAbove(tip, spotify);
      pulseTarget(spotify);

      if (!showHint('rave_mix', tip, 8000)) {
        if (tip.parentNode) tip.parentNode.removeChild(tip);
      }
    }, 10000);
  }

  // 7. outfit_guide — 2+ PDPs in festival-related collection, guide link exists
  function setupOutfitGuide() {
    if (isDismissed('outfit_guide')) return;

    var views = parseInt(sGet('pdp_views') || '0', 10);
    if (views < 2) return;

    var guideLink = document.querySelector('.fu-guide-link');
    if (!guideLink) return;

    var tip = makeEl('div', 'rg-tooltip',
      'See the <span class="rg-accent">full outfit guide</span> for this drop');
    addDismiss(tip, 'outfit_guide');
    tip.addEventListener('click', function () {
      closeHint('click');
      guideLink.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(function () { guideLink.click(); }, 600);
    });
    document.body.appendChild(tip);
    positionAbove(tip, guideLink);
    glowTarget(guideLink);

    if (!showHint('outfit_guide', tip, 8000)) {
      // Already shown this session — remove orphaned element
      if (tip.parentNode) tip.parentNode.removeChild(tip);
    }
  }

  // 8. process_journey — scroll to process journey section
  function setupProcessJourney() {
    if (isDismissed('process_journey')) return;

    // Skip on sourced products
    var tags = CFG.productTags || [];
    if (tags.indexOf('sourced') !== -1) return;

    var section = document.getElementById('pj-root');
    if (!section) return;

    var observer = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();

      if (activeHint) return; // Don't interrupt
      if (sGet('process_journey_shown')) return;

      var chip = makeEl('div', 'rg-chip',
        '<span class="rg-accent">See how your piece comes to life</span> — the full journey');
      chip.addEventListener('click', function () {
        closeHint('click');
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      section.parentNode.insertBefore(chip, section);

      if (!showHint('process_journey', chip, 8000)) {
        if (chip.parentNode) chip.parentNode.removeChild(chip);
      }
    }, { threshold: 0.1 });
    observer.observe(section);
  }

  // ─── CART HINT ───

  // 9. size_check — cart:update + Perfect Fit profile exists
  function setupSizeCheck() {
    document.addEventListener('cart:update', function (e) {
      if (!hasPerfectFit()) return;
      if (!IS_PRODUCT) return;

      // Read the PF recommended size from the DOM
      var recEl = document.getElementById('pf-result-size');
      if (!recEl) return;
      var recSize = (recEl.textContent || '').trim().toUpperCase();
      if (!recSize) return;

      // Read the selected variant size from the variant picker
      var selectedSize = '';
      var fieldsets = document.querySelectorAll('variant-picker fieldset');
      for (var i = 0; i < fieldsets.length; i++) {
        var legend = fieldsets[i].querySelector('legend');
        if (legend && /size/i.test(legend.textContent)) {
          var checked = fieldsets[i].querySelector('input[type="radio"]:checked');
          if (checked) selectedSize = checked.value.trim().toUpperCase();
          break;
        }
      }
      if (!selectedSize) return;

      // Close any existing hint first
      if (activeHint) closeHint('auto');

      var isMatch = selectedSize === recSize;
      var toast = makeEl('div',
        'rg-toast ' + (isMatch ? 'rg-toast--match' : 'rg-toast--mismatch'),
        isMatch
          ? '<span class="rg-accent">&#10003; ' + selectedSize + '</span> — your recommended size'
          : 'Heads up — your fit profile says <span class="rg-accent">' + recSize + '</span>'
      );
      addDismiss(toast, 'size_check');
      document.body.appendChild(toast);

      // size_check doesn't use permanent dismiss — only auto-dismiss
      activeHint = { id: 'size_check', el: toast };
      track('shown', 'size_check', { match: isMatch, selected: selectedSize, recommended: recSize });
      activeTimer = setTimeout(function () { closeHint('auto'); }, 5000);
    });
  }

  // ─── TRACK PRODUCT CLICKS ───
  function trackProductClicks() {
    var grid = document.querySelector('.product-grid');
    if (!grid) return;
    grid.addEventListener('click', function (e) {
      if (e.target.closest('.product-grid__item')) sSet('product_clicked');
    });
  }

  // ─── BOOT ───
  function boot() {
    // Increment page counters
    if (IS_COLLECTION) sInc('collection_visits');
    if (IS_PRODUCT) sInc('pdp_views');

    if (IS_COLLECTION) {
      trackProductClicks();
      // Comeback budget fires immediately for return visitors
      setupComebackBudget();
      // Budget intro needs scroll — sets up observer
      setupBudgetIntro();
      // Persona nudge — 2nd visit check
      setupPersonaNudge();
    }

    if (IS_PRODUCT) {
      setupFitFinder();
      setupFestivalMode();
      setupRaveMix();
      setupOutfitGuide();
      setupProcessJourney();
    }

    // Cart hint works on PDP
    setupSizeCheck();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(boot, 200); });
  } else {
    setTimeout(boot, 200);
  }
})();
